import React, { useEffect, useState } from "react";
import cn from "classnames";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell } from "recharts";
import useDarkMode from "use-dark-mode";
import Card from "../../../components/Card";
import { fetchGamesDaily } from "../../../app/stats/stats.slice";
import styles from "./GamesDaily.module.sass";

const RANGES = [7, 14, 30];

const GamesDaily = ({ className }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const darkMode = useDarkMode(false);
  const [range, setRange] = useState(7);
  const { value, isLoading, error } = useSelector((s) => s.stats.gamesDaily);

  useEffect(() => {
    dispatch(fetchGamesDaily(range));
  }, [dispatch, range]);

  const days = value?.days ?? [];
  const data = days.map((d) => ({
    label: fmtDay(d.date),
    count: d.count,
    date: d.date,
  }));
  const maxIdx = data.reduce((mi, d, i, arr) => (d.count > arr[mi].count ? i : mi), 0);
  const total = data.reduce((sum, d) => sum + d.count, 0);

  return (
    <Card
      className={cn(styles.card, className)}
      title={t("home.games.title", "Partidas jugadas")}
      classTitle="title-purple"
      head={
        <div className={styles.headRow}>
          {total > 0 && (
            <div className={styles.head}>
              <strong>{total.toLocaleString("es-ES")}</strong>{" "}
              {t("home.games.total", "partidas")}
            </div>
          )}
          <div className={styles.ranges}>
            {RANGES.map((n) => (
              <button
                key={n}
                type="button"
                className={cn(styles.rangeBtn, { [styles.rangeActive]: n === range })}
                onClick={() => setRange(n)}
              >
                {n} d
              </button>
            ))}
          </div>
        </div>
      }
    >
      <div className={styles.chart}>
        {isLoading && data.length === 0 && (
          <div className={styles.state}>{t("home.games.loading", "Cargando…")}</div>
        )}
        {error && <div className={styles.state}>{t("home.games.error", "No se pudo cargar el gráfico")}</div>}
        {!error && data.length > 0 && (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data} margin={{ top: 12, right: 12, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="gamesBar" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#B1E5FC" />
                  <stop offset="100%" stopColor="#B1E5FC" stopOpacity={0.45} />
                </linearGradient>
                <linearGradient id="gamesBarMax" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#2A85FF" />
                  <stop offset="100%" stopColor="#0069F6" />
                </linearGradient>
              </defs>
              <CartesianGrid
                strokeDasharray="3 3"
                vertical={false}
                stroke={darkMode.value ? "#33383F" : "#EFEFEF"}
              />
              <XAxis
                dataKey="label"
                tickLine={false}
                axisLine={false}
                tick={{ fontSize: 12, fill: "#9A9FA5" }}
                minTickGap={12}
              />
              <YAxis tickLine={false} axisLine={false} tick={{ fontSize: 12, fill: "#9A9FA5" }} width={40} />
              <Tooltip
                cursor={{ fill: darkMode.value ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)" }}
                content={({ active, payload }) =>
                  active && payload?.[0] ? (
                    <div className={styles.tooltip}>
                      <div className={styles.tooltipDate}>{payload[0].payload.label}</div>
                      <div className={styles.tooltipCount}>
                        <span className={styles.dot} /> {payload[0].value.toLocaleString("es-ES")}
                      </div>
                    </div>
                  ) : null
                }
              />
              <Bar dataKey="count" radius={[6, 6, 0, 0]} maxBarSize={40}>
                {data.map((_, i) => (
                  <Cell key={i} fill={i === maxIdx ? "url(#gamesBarMax)" : "url(#gamesBar)"} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
        {!isLoading && !error && data.length === 0 && (
          <div className={styles.state}>{t("home.games.empty", "Sin datos")}</div>
        )}
      </div>
    </Card>
  );
};

const MESES = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
const fmtDay = (iso) => {
  if (!iso) return "";
  const [, m, d] = iso.split("-");
  return `${Number(d)} ${MESES[Number(m) - 1]}`;
};

export default GamesDaily;
